// Shared assign / reassign / pick-up modal (F-ASGN-*). The WRITE guards live in
// data/assign.js (claim-safe TAKEN, expected-owner STALE, note-required when
// moving another's patient); this module is only the dialog UI over them, so any
// surface — Today, the workspace header, the registry — opens the same modal and
// the same guaranteed semantics apply.
//
// Ported semantics (legacy openAssignModal / execAssignSingle): one "Reassign"
// verb open to all staff; a note is REQUIRED (modal stays open) when moving a
// colleague's patient; a failed save keeps the modal + typed note intact
// (setBtnBusy pattern); a guard miss (TAKEN/STALE) closes + informs; success
// fires onDone so the caller can refresh.

import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { store, SLICES } from '../state/store.js';
import { reassignCase, pickUpCase } from '../data/assign.js';
import { assignableUsers, usersMap, userName, shortName } from '../data/users.js';

function selfId() {
  const s = store.get(SLICES.SESSION) || {};
  return s.user && s.user.id;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}

/**
 * Open the assign / reassign dialog.
 * @param {string} caseId
 * @param {string} name patient name (dialog subtitle)
 * @param {string|null} curOwner current owner id (null = unassigned → Assign)
 * @param {{onDone?: () => void}} [opts] onDone fires after a landed write / guard-miss refresh
 * @returns {void}
 */
export function openAssignModal(caseId, name, curOwner, opts) {
  if (!caseId) return;
  const me = selfId();
  const movingOthers = !!curOwner && curOwner !== me;
  const options = assignableUsers(usersMap())
    .map((u) => `<option value="${esc(u.user_id)}">${esc(u.full_name || '—')} (${esc(u.role || 'nurse')})</option>`)
    .join('');
  const noteHint = movingOthers
    ? `<p class="ws-assign-hint">You’re moving ${esc(shortName(userName(curOwner)))}’s patient — say why.</p>`
    : '';
  const body =
    '<p class="ws-assign-lead">Choose the nurse who will own this case.</p>' +
    '<div class="field"><label>Assign to</label>' +
    `<select id="ws-assign-nurse"><option value="">Select nurse…</option>${options}</select></div>` +
    `<div class="field"><label>Note for the timeline${movingOthers ? ' *' : ''}</label>` +
    '<textarea id="ws-assign-note" rows="3" placeholder="Add a note for the timeline (optional)"></textarea>' +
    noteHint +
    '</div>';

  openModal(curOwner ? 'Reassign patient' : 'Assign patient', name || '', body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: curOwner ? 'Move…' : 'Assign', v: 'pri', k: 'assign', fn: () => submitAssign(caseId, curOwner, opts) },
  ]);
}

async function submitAssign(caseId, curOwner, opts) {
  const d = doc();
  const sel = d && d.getElementById('ws-assign-nurse');
  const uid = sel ? sel.value : '';
  if (!uid) {
    toast('Select a nurse', 'err');
    return;
  }
  const noteEl = d && d.getElementById('ws-assign-note');
  const note = (noteEl && noteEl.value.trim()) || '';
  setBtnBusy(true, 'Assigning…', 'assign');
  const res = await reassignCase(caseId, uid, { curOwner, note }, undefined);
  const done = opts && opts.onDone;
  if (res.ok) {
    closeModal();
    const moved = !!curOwner;
    const okMsg = moved
      ? uid === selfId()
        ? 'Added to your queue'
        : 'Moved to ' + userName(uid)
      : 'Assigned to ' + userName(uid);
    if (res.data && res.data.noteFailed) {
      toast(okMsg + ' — but the note did NOT save. Add it via Patient notes.', 'err', 12000);
    } else {
      toast(okMsg, 'ok');
    }
    if (done) done();
    return;
  }
  if (res.code === 'TAKEN') {
    setBtnBusy(false);
    closeModal();
    toast('A colleague already picked this patient up — refreshing', 'err');
    if (done) done();
  } else if (res.code === 'STALE') {
    setBtnBusy(false);
    closeModal();
    toast('Ownership just changed — refreshing', 'err');
    if (done) done();
  } else if (res.code === 'NOTE_REQUIRED') {
    setBtnBusy(false);
    toast('You’re moving another nurse’s patient — say why', 'err');
  } else {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not assign patient'), 'err');
  }
}

/**
 * Self-claim an unassigned patient (claim-safe). TAKEN informs; success + miss
 * both call onDone so the caller refreshes. A button lock is the caller's job.
 * @param {string} caseId
 * @param {{onDone?: () => void}} [opts]
 * @returns {Promise<void>}
 */
export async function pickUp(caseId, opts) {
  const res = await pickUpCase(caseId);
  if (res.ok) toast('Patient assigned to you', 'ok');
  else if (res.code === 'TAKEN') toast('A colleague already picked this patient up — refreshing', 'err');
  else toast(netErr(res.error, 'Could not pick up this patient'), 'err');
  if (opts && opts.onDone) opts.onDone();
}
